import { Directive, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import {
  CheckConfirmCode,
  CheckConfirmCodeModel,
  CheckResetPasswordCode,
  CheckTelegramBotExist,
  CheckUserExist,
  GetConfirmRegistrationCodeToTelegramBot,
  GetConfirmRegistrationCodeToTelegramBotFail,
  GetConfirmRegistrationCodeToTelegramBotSuccess,
  GpAuthState,
  TYPE_ACTION
} from 'gp-auth';
import { RtFormLabelsFromForm, RtFormPlaceholdersFromForm, RtValidationMessagesFromForm } from 'rt-inputs';
import { combineLatest, distinctUntilChanged, map, Observable, Subscription } from 'rxjs';

@Directive({
  standalone: true
})
export abstract class GetConfirmCodeDirective implements OnInit, OnDestroy {
  isUserExist$ = this.store.select(GpAuthState.isUserExist);
  checkUserExistStatus$ = this.store.select(GpAuthState.checkUserExistStatus);
  isTelegramBotExist$ = this.store.select(GpAuthState.isTelegramBotExist);
  checkTelegramBotExistStatus$ = this.store.select(GpAuthState.checkTelegramBotExistStatus);
  telegramBotLink$ = this.store.select(GpAuthState.telegramBotLink);
  telegramBotListStatus$ = this.store.select(GpAuthState.telegramBotListStatus);
  sendConfirmCodeStatus$ = this.store.select(GpAuthState.sendConfirmCodeStatus);
  token$ = this.store.select(GpAuthState.registrationToken);

  telegramBotIsAlready$: Observable<boolean>;
  protected typeAction: TYPE_ACTION;

  public isAlreadyGetConfirmCodeForRegistration$: Observable<boolean>;

  public readonly subscription = new Subscription();

  public form = new FormGroup({
    tel: new FormControl('', [Validators.required, Validators.maxLength(10), Validators.minLength(10)]),
    confirmCode: new FormControl('', [Validators.required, Validators.maxLength(6), Validators.minLength(4)])
  });

  public labels: RtFormLabelsFromForm<typeof this.form> = {
    tel: `Phone number`,
    confirmCode: `Confirm code`
  };

  public validationMessages: RtValidationMessagesFromForm<typeof this.form> = {
    tel: {
      required: `Phone number is required`,
      maxlength: `Phone number should not be more than 10 characters`,
      minlength: `Phone number should not be less than 10 characters`
    },
    confirmCode: {
      required: `Confirm code is required`,
      maxlength: `Confirm code should not be more than 6 characters`,
      minlength: `Confirm code should not be less than 4 characters`
    }
  };

  public placeholders: RtFormPlaceholdersFromForm<typeof this.form> = {
    tel: 'Enter phone number',
    confirmCode: 'Enter confirm code'
  };

  protected constructor(protected store: Store, protected actions$: Actions) {
  }

  ngOnInit() {
    this.form.controls.confirmCode.disable();

    this.telegramBotIsAlready$ = combineLatest([this.isTelegramBotExist$, this.telegramBotListStatus$, this.form.controls.tel.valueChanges])
      .pipe(
        map(([isExist, list, tel]) => (isExist || !!list?.[`38${tel}`]))
      );


    this.isAlreadyGetConfirmCodeForRegistration$ = combineLatest([this.isUserExist$, this.telegramBotIsAlready$])
      .pipe(
        map(([isUserExist, telegramAlready]) => !isUserExist && telegramAlready),
        distinctUntilChanged()
      );

    this.subscription.add(
      this.actions$.pipe(ofActionSuccessful(GetConfirmRegistrationCodeToTelegramBotSuccess, GetConfirmRegistrationCodeToTelegramBotFail))
        .subscribe({
          next: () => {
            this.form.enable({ emitEvent: false });
          }
        })
    );

    this.subscription.add(
      this.form.controls.tel.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe({
          next: (tel) => {
            this.form.controls.confirmCode.disable({ emitEvent: false });
            if (this.form.valid) {
              this.store.dispatch(new CheckUserExist(tel));
              this.store.dispatch(new CheckTelegramBotExist(tel));
            }
          }
        })
    );

  }

  sendConfirmCode() {
    if (this.form.valid) {
      this.store.dispatch(new GetConfirmRegistrationCodeToTelegramBot(this.form.value.tel));
      this.form.disable({ emitEvent: false });
    }
  }

  checkConfirmCode() {
    if (this.form.valid) {
      switch (this.typeAction) {
        case TYPE_ACTION.REGISTRATION:
          this.store.dispatch(new CheckConfirmCode(this.form.value as CheckConfirmCodeModel));
          break;
        case TYPE_ACTION.RESET_PASSWORD:
          this.store.dispatch(new CheckResetPasswordCode(this.form.value as CheckConfirmCodeModel));
      }
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

}
